import { z } from 'zod';
import { localizedTextSchema } from './locale';

export const questionKindSchema = z.enum(['single', 'truefalse', 'multi', 'numeric', 'ordering']);
export type QuestionKind = z.infer<typeof questionKindSchema>;

export const QUESTION_KINDS = [
  'single',
  'truefalse',
  'multi',
  'numeric',
  'ordering',
] as const satisfies readonly QuestionKind[];

const choiceSchema = z.object({ id: z.string().min(1), label: localizedTextSchema }).strict();

const baseQuestion = {
  id: z.string().min(1),
  prompt: localizedTextSchema,
  chapterId: z.string().min(1).optional(),
};

/**
 * `correct` (and `tolerance`) are SERVER-ONLY - stripped from the client
 * payload by BozpTrainingController.getTraining. Authored here + stored in the
 * DB only. Per-kind shapes mirror the locked-contract QuestionKind grader.
 */
const singleQuestion = z
  .object({
    ...baseQuestion,
    kind: z.literal('single'),
    choices: z.array(choiceSchema).min(2),
    correct: z.string().min(1),
  })
  .strict();
const truefalseQuestion = z
  .object({ ...baseQuestion, kind: z.literal('truefalse'), correct: z.boolean() })
  .strict();
const multiQuestion = z
  .object({
    ...baseQuestion,
    kind: z.literal('multi'),
    choices: z.array(choiceSchema).min(2),
    correct: z.array(z.string().min(1)).min(1),
  })
  .strict();
const numericQuestion = z
  .object({
    ...baseQuestion,
    kind: z.literal('numeric'),
    correct: z.number(),
    tolerance: z.number().nonnegative().optional(),
  })
  .strict();
const orderingQuestion = z
  .object({
    ...baseQuestion,
    kind: z.literal('ordering'),
    choices: z.array(choiceSchema).min(2),
    correct: z.array(z.string().min(1)).min(2),
  })
  .strict();

export const questionSchema = z.discriminatedUnion('kind', [
  singleQuestion,
  truefalseQuestion,
  multiQuestion,
  numericQuestion,
  orderingQuestion,
]);

export type Question = z.infer<typeof questionSchema>;

/**
 * CLIENT view - the answer key (`correct`/`tolerance`) is stripped server-side by
 * BozpTrainingController.getTraining. The renderer + player type their questions with
 * this DISTRIBUTIVE union (per-kind `.omit` re-unioned) so a stripped question keeps its
 * per-kind `choices`. A flat `Omit<Question,'correct'|'tolerance'>` would collapse the
 * union and drop `choices` (a non-distributive `Omit` keeps only keys common to ALL members).
 */
export const clientQuestionSchema = z.discriminatedUnion('kind', [
  singleQuestion.omit({ correct: true }),
  truefalseQuestion.omit({ correct: true }),
  multiQuestion.omit({ correct: true }),
  numericQuestion.omit({ correct: true, tolerance: true }),
  orderingQuestion.omit({ correct: true }),
]);
export type ClientQuestion = z.infer<typeof clientQuestionSchema>;
